/**
 * The page behind a guest link.
 *
 * Someone with no ERP account — a client, usually — opens this, types a name
 * and lands in the one call the link was cut for. There is no session here and
 * nothing else to reach: the link is the whole authorization, our backend
 * checks it and hands back a short-lived token for that room alone.
 *
 * The call itself runs on the same engine as everywhere else, served by the
 * calls service, and the same tiles the ERP uses — nothing about a call is
 * written twice.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mic, MicOff, Monitor, MonitorOff, PhoneOff, Video, VideoOff } from "lucide-react";
import type { CallsRoom, CallState, RoomParticipant } from "calls-sdk";
import { joinCallAsGuest } from "services/calls.service";
import { loadCallsSdk } from "./sdk";
import { AudioSink, CallStage, PeerTile, canShareScreen } from "./call-tiles";
import { MicHelpSteps } from "./call-failure";

type Stage = "form" | "joining" | "in-call" | "left" | "error";

// 44px round buttons for a finger on a phone, 40px from the md breakpoint up.
const CONTROL_BUTTON =
  "flex h-11 w-11 items-center justify-center rounded-full md:h-10 md:w-10";

const GuestCall: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();

  const [stage, setStage] = useState<Stage>("form");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** the error is a refused microphone — the one a guest can fix themselves */
  const [micError, setMicError] = useState(false);
  const [engineState, setEngineState] = useState<CallState | null>(null);
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);
  const [streams, setStreams] = useState<Record<string, MediaStream>>({});
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [stagePeer, setStagePeer] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const [sharing, setSharing] = useState(false);

  const roomRef = useRef<CallsRoom | null>(null);
  const localRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = localRef.current;
    if (!el) return;
    if (el.srcObject !== (localStream ?? null)) el.srcObject = localStream ?? null;
  }, [localStream]);

  useEffect(() => {
    if (stagePeer && !streams[stagePeer]) setStagePeer(null);
  }, [stagePeer, streams]);

  const leave = useCallback(() => {
    const room = roomRef.current;
    roomRef.current = null;
    void room?.hangUp(true);
    setStreams({});
    setParticipants([]);
    setLocalStream(null);
    setStage("left");
  }, []);

  // a guest closing the tab should not linger in the roster
  useEffect(() => {
    const onUnload = () => void roomRef.current?.hangUp(true);
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  const join = useCallback(async () => {
    setStage("joining");
    setError(null);
    setMicError(false);
    setEngineState(null);
    try {
      const pass = await joinCallAsGuest(token, name.trim());
      const sdk = await loadCallsSdk(pass.sdkUrl);
      const room = await sdk.CallsRoom.connect({
        url: pass.url,
        token: pass.token,
        handlers: {
          // only to tell "waiting for your microphone" from "connecting"
          onState: (s) => setEngineState(s),
          onParticipants: (list) => setParticipants(list),
          onStream: (peerId, stream) =>
            setStreams((prev) => ({ ...prev, [peerId]: stream })),
          onStreamGone: (peerId) =>
            setStreams((prev) => {
              const next = { ...prev };
              delete next[peerId];
              return next;
            }),
          onLocalVideo: (track) => {
            setLocalStream(track ? new MediaStream([track]) : null);
            setVideoOn(!!roomRef.current?.videoOn);
            setSharing(!!roomRef.current?.sharing);
          },
          onEnded: () => leave(),
          onError: (kind) => {
            if (kind === "camera") {
              // an extra, not the call: the voices carry on without it
              setError(t("call_camera_denied"));
              setVideoOn(false);
              setSharing(false);
              return;
            }
            setError(kind === "mic" ? t("call_mic_denied") : t("call_failed"));
            setMicError(kind === "mic");
            leave();
          },
        },
      });
      roomRef.current = room;
      const ok = await room.joinGroup();
      // `ok` false, or something tore us down while joining (a mic refusal
      // runs `leave`): either way close the room rather than leave a silent
      // guest sitting in the host's roster until the tab closes.
      if (!ok || roomRef.current !== room) {
        roomRef.current = null;
        void room.hangUp(true);
        throw new Error("join failed");
      }
      setStage("in-call");
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const generic =
        status === 404
          ? t("call_guest_over")
          : status === 403
            ? t("call_guest_link_invalid")
            : t("call_failed");
      // a refused microphone already said so on its way through `onError`;
      // "the call failed" would be true but useless next to it
      setError((prev) => prev || generic);
      setStage("error");
    }
  }, [leave, name, t, token]);

  const controls = (
    <>
      <button
        onClick={() => setMuted(roomRef.current?.toggleMute() ?? false)}
        className={`${CONTROL_BUTTON} bg-gray-700 text-gray-100 hover:bg-gray-600`}
        title={muted ? t("call_unmute") : t("call_mute")}
      >
        {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
      </button>
      <button
        onClick={() => {
          const room = roomRef.current;
          if (!room) return;
          setError(null); // a new attempt replaces whatever the last one said
          void (room.videoOn && !room.sharing ? room.stopVideo() : room.enableCamera());
        }}
        className={`${CONTROL_BUTTON} bg-gray-700 text-gray-100 hover:bg-gray-600`}
        title={videoOn && !sharing ? t("call_camera_off") : t("call_camera_on")}
      >
        {videoOn && !sharing ? (
          <VideoOff className="h-5 w-5" />
        ) : (
          <Video className="h-5 w-5" />
        )}
      </button>
      {canShareScreen() && (
        <button
          onClick={() => {
            const room = roomRef.current;
            if (!room) return;
            setError(null);
            void (room.sharing ? room.stopVideo() : room.shareScreen()).catch(() =>
              setError(t("call_screen_unavailable"))
            );
          }}
          className={`${CONTROL_BUTTON} bg-gray-700 text-gray-100 hover:bg-gray-600`}
          title={sharing ? t("call_screen_stop") : t("call_screen_share")}
        >
          {sharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
        </button>
      )}
      <button
        onClick={leave}
        className={`${CONTROL_BUTTON} bg-red-600 text-white hover:bg-red-500`}
        title={t("call_hang_up")}
      >
        <PhoneOff className="h-5 w-5" />
      </button>
    </>
  );

  if (stage === "in-call") {
    const staged = stagePeer
      ? participants.find((p) => p.userId === stagePeer)
      : undefined;
    // shown on the panel — and again over the enlarged view, which covers the
    // whole viewport, this banner included
    const notice = error ? (
      <div className="flex items-center justify-between gap-3 rounded-lg bg-red-950/60 px-3 py-2 text-sm text-red-200 ring-1 ring-red-900">
        <span className="min-w-0 truncate">{error}</span>
        <button
          type="button"
          onClick={() => setError(null)}
          className="shrink-0 rounded px-2 py-0.5 text-xs text-red-200/80 hover:bg-red-900/60 max-md:min-h-11 max-md:px-3"
        >
          {t("close")}
        </button>
      </div>
    ) : null;
    return (
      <div className="min-h-dvh bg-gray-900 p-4 text-gray-100">
        {Object.entries(streams).map(([peerId, stream]) => (
          <AudioSink key={peerId} stream={stream} />
        ))}
        {staged && (
          <CallStage
            name={staged.name}
            screen={!!staged.screen}
            stream={streams[staged.userId]}
            controls={controls}
            notice={notice}
            onClose={() => setStagePeer(null)}
          />
        )}
        <div className="mx-auto max-w-3xl">
          <div className="mb-3 text-sm text-gray-400">{t("call_group_title")}</div>
          {/* a camera that refuses does not end the call, so this is the only
              place the guest would ever learn that it did */}
          {notice && <div className="mb-3">{notice}</div>}
          <div
            className={`grid gap-3 ${participants.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}
          >
            {participants.map((p) => (
              <PeerTile
                key={p.userId}
                name={p.name}
                stream={streams[p.userId]}
                hasVideo={!!p.video}
                screen={!!p.screen}
                onExpand={() => setStagePeer(p.userId)}
              />
            ))}
            {localStream && (
              <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-950 ring-1 ring-gray-700">
                <video
                  ref={localRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                />
                <div className="absolute bottom-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[11px]">
                  {sharing ? t("call_your_screen") : t("call_you")}
                </div>
              </div>
            )}
          </div>
          {/* on a phone it stays at the bottom of the screen while a big group's pictures scroll under it */}
          <div className="sticky bottom-0 -mx-4 mt-5 flex flex-wrap items-center justify-center gap-3 bg-gray-900 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:static md:mx-0 md:p-0">
            {controls}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-900 p-4 text-gray-100">
      <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6 text-center shadow-2xl">
        <div className="text-lg font-semibold">
          {stage === "left" ? t("call_guest_left") : t("call_guest_title")}
        </div>
        {error && <div className="mt-3 text-sm text-red-400">{error}</div>}
        {micError && (
          <MicHelpSteps className="mt-2 text-left text-xs text-gray-300" />
        )}
        {(stage === "form" || stage === "joining") && (
          <>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("call_guest_name")}
              maxLength={60}
              className="mt-5 w-full rounded-lg bg-gray-900 px-3 py-2 text-base text-gray-100 outline-none ring-1 ring-gray-700 focus:ring-blue-500 md:text-sm"
            />
            <button
              onClick={() => void join()}
              disabled={stage === "joining"}
              className="mt-3 w-full rounded-lg bg-green-600 px-3 py-3 text-sm font-medium text-white hover:bg-green-500 disabled:opacity-50 md:py-2"
            >
              {stage !== "joining"
                ? t("call_guest_join")
                : engineState === "permission"
                ? t("call_mic_permission")
                : t("call_connecting")}
            </button>
            <div className="mt-3 text-xs text-gray-500">{t("call_guest_mic_hint")}</div>
          </>
        )}
        {stage === "error" && (
          <button
            onClick={() => setStage("form")}
            className="mt-5 w-full rounded-lg bg-gray-700 px-3 py-3 text-sm text-gray-100 hover:bg-gray-600 md:py-2"
          >
            {t("call_guest_join")}
          </button>
        )}
      </div>
    </div>
  );
};

export default GuestCall;
